'use client';

import { useEffect, useState } from 'react';

import { PushOptInCard } from '#/components/push/push-opt-in-card';
import { useFloatingPromptSlot } from '#/lib/floating-prompt';
import {
  hasPushOptInRecord,
  recordPushOptInDecision
} from '#/lib/push/opt-in-record';

/** 사람이 실제로 손을 댄 뒤 이만큼 지나서 띄운다. */
const APPEAR_DELAY_MS = 1200;

/** 조작으로 칠 신호. 스크롤은 넣지 않는다 — 화면을 훑기만 해도 떠 버린다. */
const INTERACTION_EVENTS = ['keydown', 'pointerdown', 'paste'] as const;

/** 구독 완료 문구를 보여 주는 시간. */
const CONFIRM_LINGER_MS = 3000;

/**
 * 도구 화면(맞춤법 검사·형태분석)에 처음 들어왔을 때 한 번 권하는 알림 구독 카드. (#264)
 *
 * 종전에는 업데이트 메뉴의 `PushOptInCard` 가 유일한 창구였는데, 그 메뉴에 들어가는
 * 사람이 거의 없어 권유가 사실상 도달하지 않았다. 사람이 실제로 들어오는 곳에서
 * 권하되 세 가지를 지킨다:
 *
 *  - **가로막지 않는다.** 모달이 아니다. 도구를 쓰러 온 사람을 막으면 알림 구독보다
 *    훨씬 큰 것을 잃는다(검사기 유입 → 로그인 전환이 3.6%).
 *  - **레이아웃을 건드리지 않는다.** 문서 흐름에 끼우면 /correct 처럼 뷰포트를 채우는
 *    화면에서 편집기가 그만큼 줄고, 하이드레이션 직후 레이아웃이 밀린다(CLS). 그래서
 *    화면에 떠 있는 카드로 둔다.
 *  - **한 번만 권한다.** 답을 받았으면(구독 또는 닫기) 기록하고 다시 묻지 않는다.
 *    마음이 바뀌면 업데이트 메뉴의 카드에서 언제든 켤 수 있다.
 *
 * 권한 요청 시점은 카드가 지키는 원칙 그대로다 — 버튼을 누른 그 순간에만(soft-ask).
 *
 * 별점 창(#291)이 떠 있는 동안에는 숨는다. 두 카드 모두 화면 아래에 떠서 모바일에서
 * 겹치기 때문이다. 판정은 `lib/floating-prompt` 한 곳이 하고, 별점 창이 닫히면 다시 보인다.
 */
export function PushOptInPrompt() {
  // 기록 조회는 마운트 이후에 한다 — 첫 렌더에서 localStorage 를 읽으면 서버 렌더
  // 결과와 달라져 hydration 이 깨진다.
  const [askable, setAskable] = useState(false);

  // 시간만 재고 띄우면 아무것도 안 한 첫 화면을 카드가 덮는다. 특히 모바일에서는
  // 입력창까지 가려서, 검사하러 온 사람이 알림 이야기를 먼저 보게 된다. 그래서
  // 실제로 손을 댄 다음에만 권한다.
  useEffect(() => {
    if (hasPushOptInRecord()) return;

    let timer = 0;
    const onInteract = () => {
      timer = window.setTimeout(() => setAskable(true), APPEAR_DELAY_MS);
      cleanup();
    };
    const cleanup = () => {
      for (const type of INTERACTION_EVENTS) {
        window.removeEventListener(type, onInteract);
      }
    };

    for (const type of INTERACTION_EVENTS) {
      window.addEventListener(type, onInteract, { once: true, passive: true });
    }

    return () => {
      cleanup();
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  // 권유할 차례여도 다른 떠 있는 카드(별점 창)가 자리를 쓰는 동안은 비켜 있는다.
  const mayShow = useFloatingPromptSlot('push-opt-in', askable);

  if (!askable || !mayShow) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md shadow-lg sm:left-auto sm:right-6 sm:mx-0">
      <PushOptInCard
        title="새 기능·문서 업데이트를 알림으로 받아 보세요"
        description="가입하지 않아도 됩니다. 이 브라우저로 다시 오시면 알려드립니다."
        onSubscribed={() => {
          recordPushOptInDecision('subscribed');
          // 확인 문구를 잠깐 보여 준 뒤 스스로 사라진다 — 떠 있는 카드가 계속
          // 남아 화면을 가리지 않도록.
          window.setTimeout(() => setAskable(false), CONFIRM_LINGER_MS);
        }}
        onDismiss={() => {
          // 닫기도 하나의 답이다 — 기록해 두고 다시 묻지 않는다.
          recordPushOptInDecision('dismissed');
          setAskable(false);
        }}
      />
    </div>
  );
}
